import { useState } from 'react';
import type { ControllerProps } from 'react-hook-form';
import { Controller, FieldValues } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { DateTimeRangePicker } from '@rossum/components/date-time-range-picker';
import { SelectedDates } from '@rossum/components/date-time-range-picker/types';
import { IconCalendarMonth } from '@rossum/ui/icons/tabler';
import { Button, Paper, Popover, Stack, SvgIcon } from '@rossum/ui/material';
import { formatDatepickerValue } from '../../../lib/dateUtils';
import {
  formatDateQuery,
  parseDateQuery,
} from '../../../redux/modules/statistics/helpers';
import { StatisticsFilterDropdown } from './StatisticsFilterDropdown';

type ControlledDatepickerProps<T extends FieldValues> = {
  ControllerProps: Omit<ControllerProps<T>, 'render'>;
};

const ControlledDatepicker = <T extends FieldValues>({
  ControllerProps,
}: ControlledDatepickerProps<T>) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedDates, setSelectedDates] = useState<SelectedDates>([
    null,
    null,
  ]);

  const { control, name } = ControllerProps;

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Controller<T>
      control={control}
      name={name}
      render={({ field }) => {
        const value = field.value as { from: string; to: string };

        const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
          setSelectedDates([new Date(value.from), new Date(value.to)]);
          setAnchorEl(event.currentTarget);
        };

        return (
          <>
            <StatisticsFilterDropdown
              open={!!anchorEl}
              onClick={handleOpen}
              startIcon={
                <SvgIcon fontSize="large">
                  <IconCalendarMonth />
                </SvgIcon>
              }
            >
              {formatDatepickerValue(
                parseDateQuery(value.from),
                parseDateQuery(value.to),
                intl.locale
              )}
            </StatisticsFilterDropdown>
            <Popover
              open={!!anchorEl}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Stack component={Paper} sx={{ p: 2, gap: 2 }}>
                <DateTimeRangePicker
                  selectedDates={selectedDates}
                  setSelectedDates={setSelectedDates}
                  operatorValue="is"
                  hideTimePickers
                  locale={intl.locale}
                />
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (selectedDates[0] && selectedDates[1]) {
                        field.onChange({
                          from: formatDateQuery(selectedDates[0]),
                          to: formatDateQuery(selectedDates[1]),
                        });
                        handleClose();
                      }
                    }}
                  >
                    {intl.formatMessage({
                      id: 'components.datepicker.ok',
                    })}
                  </Button>
                </Stack>
              </Stack>
            </Popover>
          </>
        );
      }}
    />
  );
};

export { ControlledDatepicker };
